import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { PageHeader, Section, ArrowLink, Eyebrow, Body } from "@/components/shared/PageHeader";
import { RoomCard } from "@/components/shared/RoomCard";
import { FadeUp } from "@/components/shared/FadeUp";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { fetchRooms, fetchContent } from "@/lib/api";
import { paragraphs } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Seo } from "@/components/shared/Seo";

const FILTERS = [["all", "Alle rom"], ["1", "1. etasje"], ["2", "2. etasje"], ["3", "3. etasje"]];

export default function Rooms() {
  const [floor, setFloor] = useState("all");
  const { data: rooms, isLoading } = useQuery({ queryKey: ["rooms"], queryFn: fetchRooms });
  const { data: content } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const list = (rooms || []).filter((r) => !r.is_whole_house && (floor === "all" || String(r.floor) === floor));
  const whole = (rooms || []).find((r) => r.is_whole_house);
  const info = content?.booking_info;

  return (
    <div data-testid="rooms-page">
      <Seo title="Rom og prisar" description="Sju rom på tre etasjar: King Oscar Amfi med 187 plassar, Foajeen, Kippers Kafé, Stovene med kjøkken og møteromma Mussa, Sardino og Brisling. Sjå kapasitet, prisar og ledige datoar." image={rooms?.find((r) => r.slug === "king-oscar-amfi")?.images?.[0]} imageAlt="King Oscar Amfi i Halsnøy Kulturhus" />
      <PageHeader eyebrow="Rom og prisar" title="Sju rom, tre etasjar – eitt hus for heile øya" intro="Amfisal, foajeen og kafé i 1. etasje. Stovene med kjøkken og møterommet Mussa i 2. etasje. Møteromma Sardino og Brisling i 3. etasje.">
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" data-testid="rooms-booking-link"><Link to="/leig-rom">Send bookingførespurnad <ArrowUpRight className="h-4 w-4" /></Link></Button>
          <Button asChild variant="outline" size="lg" data-testid="rooms-prices-link"><Link to="/prisar">Full prisliste</Link></Button>
        </div>
      </PageHeader>

      <Section className="pt-6 sm:pt-8 lg:pt-10">
        <div className="mb-8 flex flex-wrap gap-2" role="tablist" data-testid="rooms-floor-filter">
          {FILTERS.map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={floor === v} onClick={() => setFloor(v)} data-testid={`rooms-filter-${v}`} className={cn("inline-flex h-10 items-center rounded-[8px] border px-4 text-[14px] font-medium transition-colors", floor === v ? "border-foreground bg-foreground text-background" : "border-border bg-card text-muted-foreground hover:border-foreground/40 hover:text-foreground")}>{l}</button>
          ))}
        </div>
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="aspect-[4/5] rounded-[20px]" />)}</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="rooms-grid">
            {list.map((r, i) => <FadeUp key={r.id} delay={i * 0.04}><RoomCard room={r} /></FadeUp>)}
          </div>
        )}

        {whole && (
          <div className="mt-14 grid gap-5 border-t border-border pt-8 lg:grid-cols-12 lg:items-center lg:gap-8" data-testid="rooms-whole-house">
            <div className="lg:col-span-3"><Eyebrow>{whole.name}</Eyebrow></div>
            <div className="lg:col-span-6">
              <h2 className="t-h3">{whole.tagline}</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{whole.description}</p>
            </div>
            <div className="lg:col-span-3 lg:text-right"><ArrowLink to={`/leig-rom?rom=${whole.id}`} data-testid="rooms-whole-house-book">Spør om heile huset</ArrowLink></div>
          </div>
        )}

        {info && (
          <div className="mt-16" data-testid="rooms-cleaning-note">
            <Body>
              <h2 className="t-h2 max-w-2xl text-balance">{info.title}</h2>
              <div className="prose-nn mt-6 max-w-2xl text-[17px] leading-relaxed text-muted-foreground text-pretty">{paragraphs(info.body).map((p, i) => <p key={i}>{p}</p>)}</div>
            </Body>
          </div>
        )}
      </Section>
    </div>
  );
}
